import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Paper,
  Divider,
  Badge
} from '@mui/material';
import {
  CalendarToday,
  AccessTime,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';
import moment from 'moment';
import { lightTheme, useThemeContext } from '../../contexts/ThemeManager/ThemeManager';
import QuickActionButton from '../QuickActionButton';
import { Request } from '../modals/ManagerApprovalModal/ManagerApprovalModal';
import { checkAccessLevel } from '../../lib/authenticate';

interface QuickActionSidebarProps {
  upcomingShifts: any[];
  nextShiftExpanded: boolean;
  selectedSchedule: any;
  setNextShiftExpanded: (expanded: boolean) => void;
  getNextRecurringShiftDate: (daysOfWeek: string[]) => moment.Moment;
  weekStats: {
    totalHours: number;
    shiftsScheduled: number;
    daysOff: number;
  };
  userData: any;
  quickActions: Array<{
    title: string;
    description: string;
    icon: any;
    color: 'success' | 'secondary' | 'error' | 'info' | 'primary' | 'warning';
    onClick: () => void;
  }>;
  requests: Request[] | null;
  accessLevel: string | null;
}

const QuickActionSidebar: React.FC<QuickActionSidebarProps> = ({
  upcomingShifts,
  nextShiftExpanded,
  selectedSchedule,
  setNextShiftExpanded,
  getNextRecurringShiftDate,
  weekStats,
  userData,
  requests,
  quickActions,
  accessLevel
}) => {
  const { isDarkMode } = useThemeContext();

  const isRequestExpired = (request: Request): boolean => {
    // For pending requests
    if (request.status === 'pending') {
      if (request.requestType === 'SWAP') {
        const swapDate = moment(request.requestDetails.swapDate);
        return swapDate.isBefore(moment(), 'day');
      }
      
      if (request.requestType === 'OFF') {
        const offDate = moment(request.requestDetails.offDate);
        return offDate.isBefore(moment(), 'day');
      }
    }
    
    // For approved swaps that are open for pickup
    if (request.status === 'approved' && request.requestType === 'SWAP') {
      const swapDate = moment(request.requestDetails.swapDate);
      return swapDate.isBefore(moment(), 'day');
    }
    
    return false;
  };
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "auto auto", bgcolor: selectedSchedule?.color + '08' }}>
      <Divider orientation="vertical" variant='middle' flexItem sx={{ display: { xs: 'none', lg: 'block', height: "100%", width: "1px" }, bgcolor: selectedSchedule?.color }} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Next Shift Card */}
        <Card
          sx={{
            background: nextShiftExpanded
              ? isDarkMode ? `linear-gradient(135deg, ${lightTheme.palette.info.main} 0%, ${lightTheme.palette.info.light} 100%)` :
                `linear-gradient(135deg, ${lightTheme.palette.secondary.main} 0%, ${lightTheme.palette.secondary.light} 100%)`
              : 'background.paper',
            color: nextShiftExpanded ? 'white' : 'inherit',
            border: nextShiftExpanded ? '2px transparent' : '2px solid',
            borderColor: isDarkMode ? 'info.main' : 'primary.main',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            '&:hover': {
              boxShadow: nextShiftExpanded ? 3 : 3,
              borderColor: nextShiftExpanded ? 'primary.main' : 'info.light'
            }
          }}
          onClick={() => !nextShiftExpanded && setNextShiftExpanded(true)}
        >
          <CardContent sx={{ p: nextShiftExpanded ? 3 : 2, '&:last-child': { pb: nextShiftExpanded ? 3 : 2 } }}>
            {nextShiftExpanded ? (
              // Expanded View
              <>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2
                  }}
                >
                  <Typography variant="caption" sx={{ opacity: 0.8, textTransform: 'uppercase', letterSpacing: 1 }}>
                    Next Shift
                  </Typography>
                  <IconButton
                    size="small"
                    sx={{ color: 'white' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setNextShiftExpanded(false);
                    }}
                  >
                    <ExpandLess />
                  </IconButton>
                </Box>

                <Typography variant="h6" fontWeight="bold">
                  {upcomingShifts[0] ? upcomingShifts[0]?.shiftName : "No upcoming shifts"}
                </Typography>

                <Typography variant="body1" fontWeight={750} color={isDarkMode ? 'info.dark' : 'secondary.dark'}>
                  {upcomingShifts[0] ? (upcomingShifts[0]?.shiftDate ? moment(upcomingShifts[0]?.shiftDate).format('dddd, MMM D') :
                    getNextRecurringShiftDate(upcomingShifts[0]?.daysOfWeek).format('dddd, MMM D')) + "," : ""}
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2, opacity: 0.9 }}>
                  {upcomingShifts[0] && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccessTime fontSize="small" />
                    <Typography variant="body2">{moment(upcomingShifts[0]?.startTime, "HH:mm").format('h:mm A')}</Typography>
                    <Typography variant="body2">-</Typography>
                    <Typography variant="body2">{moment(upcomingShifts[0]?.endTime, "HH:mm").format('h:mm A')}</Typography>
                  </Box>}
                </Box>

                <Paper sx={{ bgcolor: 'rgba(255, 255, 255, 0.37)', p: 2, textAlign: 'center', mb: 2 }}>
                  {upcomingShifts[0] ? <Typography variant="h4" fontWeight="bold" color={isDarkMode ? 'info.dark' : 'secondary.dark'}>{Math.abs(moment().diff(upcomingShifts[0]?.shiftDate ? moment(upcomingShifts[0]?.shiftDate).set("hour", moment(upcomingShifts[0]?.startTime, "HH:mm").hour()).set("minute", moment(upcomingShifts[0]?.startTime, "HH:mm").minute()) : getNextRecurringShiftDate(upcomingShifts[0]?.daysOfWeek).set("hour", moment(upcomingShifts[0]?.startTime, "HH:mm").hour()).set("minute", moment(upcomingShifts[0]?.startTime, "HH:mm").minute()), 'hours'))}h</Typography>
                    : <Typography variant="h6" fontWeight="bold" color={isDarkMode ? 'info.dark' : 'secondary.dark'}>--</Typography>}
                  <Typography variant="caption" color='text.secondary'>until shift</Typography>
                </Paper>
              </>
            ) : (
              // Collapsed View - Button Size
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  <CalendarToday sx={{ color: isDarkMode ? 'info.main' : 'secondary.main', fontSize: 20 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={700} color={isDarkMode ? 'info.main' : 'secondary.main'} noWrap>
                    Next Shift
                  </Typography>
                  {upcomingShifts[0] ? <Typography variant="caption" color="text.primary" noWrap>
                    {upcomingShifts[0]?.shiftDate ? moment(upcomingShifts[0]?.shiftDate).format('dddd, MMM D') : getNextRecurringShiftDate(upcomingShifts[0]?.daysOfWeek).format('dddd, MMM D')} •
                    {Math.abs(moment().diff(upcomingShifts[0]?.shiftDate ? moment(upcomingShifts[0]?.shiftDate).set("hour", moment(upcomingShifts[0]?.startTime, "HH:mm").hour()).set("minute", moment(upcomingShifts[0]?.startTime, "HH:mm").minute()) :
                      getNextRecurringShiftDate(upcomingShifts[0]?.daysOfWeek).set("hour", moment(upcomingShifts[0]?.startTime, "HH:mm").hour()).set("minute", moment(upcomingShifts[0]?.startTime, "HH:mm").minute()), 'hours'))}h away
                  </Typography> : <Typography variant="caption" color="text.primary" noWrap>
                    No upcoming shifts
                  </Typography>}
                </Box>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setNextShiftExpanded(true);
                  }}
                >
                  <ExpandMore />
                </IconButton>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        {quickActions.map((action, index) => {
          // Check if this is the "Manage Requests" action
          if (checkAccessLevel(accessLevel) && action.title === 'Manage Requests') {
            let pendingCount = requests?.filter(r => r.status === 'pending' && !isRequestExpired(r)).length || 0;
            pendingCount += requests?.filter(r =>
              r.requestType === 'SWAP' &&
              r.status === 'approved' &&
              ((r.requesterId !== userData.userId && r.requestDetails.swapTarget === userData.userId) || r.requestDetails.swapTarget === 'any') &&
              !isRequestExpired(r)
            ).length || 0
            return (
              <Badge
                key={"qa-sidebar-" + index}
                badgeContent={pendingCount}
                color="error"
                sx={{
                  width: '100%',
                  '& .MuiBadge-badge': {
                    top: 16,
                    right: 150,
                    border: '2px solid background.default',
                    color: 'white'
                  }
                }}
              >
                <QuickActionButton
                  title={action.title}
                  description={action.description}
                  icon={action.icon}
                  color={action.color}
                  isDarkMode={isDarkMode}
                  onClick={action.onClick}
                />
              </Badge>
            );
          }

          return (
            <QuickActionButton
              key={"qa-sidebar-" + index}
              title={action.title}
              description={action.description}
              icon={action.icon}
              color={action.color}
              isDarkMode={isDarkMode}
              onClick={action.onClick}
            />
          );
        })}
      </Box>
    </Box>
  );
};

export default QuickActionSidebar;